import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import Message from './Message';
import PaymentModal from './PaymentModal';

function ChatContainer({ messages, onSendMessage, onToggleSettings, onOpenPaymentModal, onA2UIEvent, userName, onNavigateToGraph, isGraphViewOpen, onCloseGraph, paymentModalData, onClosePaymentModal, onConfirmPayment, userId, sessionId, userInfo, updateUserInfo }) {
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const adjustTextareaHeight = (textarea) => {
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
    }
  };

  useEffect(() => {
    if (textareaRef.current) {
      adjustTextareaHeight(textareaRef.current);
    }
  }, [inputValue]);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      // Direct scrollTop assignment is faster and more reliable for streaming
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    } else if (messagesEndRef.current) {
      // Fallback to scrollIntoView
      messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
    }
  };

  // Use useLayoutEffect to scroll immediately after DOM updates (for streaming)
  useLayoutEffect(() => {
    // Use requestAnimationFrame to ensure DOM is fully updated before scrolling
    requestAnimationFrame(() => {
      scrollToBottom();
    });
  }, [messages]);

  // Also scroll when messages array length changes
  useEffect(() => {
    scrollToBottom();
  }, [messages.length]);

  const handleSend = async () => {
    if (!inputValue.trim() || isSending) return;

    const messageToSend = inputValue;
    setInputValue(''); // Clear input immediately
    setIsSending(true);
    
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    
    await onSendMessage(messageToSend);
    setIsSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const Icon = ({ name }) => {
    const common = {
      className: 'icon',
      width: 18,
      height: 18,
      viewBox: '0 0 24 24',
      fill: 'none',
      xmlns: 'http://www.w3.org/2000/svg',
      'aria-hidden': true
    };

    if (name === 'settings') {
      return (
        <svg {...common}>
          <path
            d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M19.4 15a8.6 8.6 0 0 0 .05-1l2.02-1.57a1 1 0 0 0 .23-1.28l-1.9-3.29a1 1 0 0 0-1.22-.44l-2.38.96a8.53 8.53 0 0 0-1.74-1.01l-.36-2.54A1 1 0 0 0 12.1 2h-3.8a1 1 0 0 0-.99.83l-.36 2.54c-.62.25-1.2.58-1.74 1.01l-2.38-.96a1 1 0 0 0-1.22.44L.7 11.15a1 1 0 0 0 .23 1.28L2.95 14c-.03.33-.05.66-.05 1s.02.67.05 1l-2.02 1.57a1 1 0 0 0-.23 1.28l1.9 3.29a1 1 0 0 0 1.22.44l2.38-.96c.54.43 1.12.76 1.74 1.01l.36 2.54a1 1 0 0 0 .99.83h3.8a1 1 0 0 0 .99-.83l.36-2.54c.62-.25 1.2-.58 1.74-1.01l2.38.96a1 1 0 0 0 1.22-.44l1.9-3.29a1 1 0 0 0-.23-1.28L19.4 16Z"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinejoin="round"
            opacity="0.95"
          />
        </svg>
      );
    }

    // name === 'graph'
    return (
      <svg {...common}>
        <path
          d="M7 18V11M12 18V6M17 18v-8"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path
          d="M5 18h14"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          opacity="0.9"
        />
      </svg>
    );
  };

  return (
    <div className="chat-container">
      {/* Header */}
      
      {/* Messages */}
      <div className="chat-messages" ref={messagesContainerRef}>
        {messages.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">💬</div>
            <p>Hello, {userName}! </p>
            <p style={{ fontSize: '13px', marginTop: '8px', opacity: 0.7 }}>
              You can ask about hotels, flights, or travel plans
            </p>
          </div>
        ) : (
          messages.map((message) => (
            <Message
              key={message.id}
              message={message}
              onOpenPaymentModal={onOpenPaymentModal}
              onA2UIEvent={onA2UIEvent}
            />
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="chat-input-container">
        <div className="input-wrapper">
          <textarea
            ref={textareaRef}
            className="chat-input"
            placeholder="Type your message here... (Press Enter to send, Shift+Enter for new line)"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            rows="1"
          />
        </div>
        <button
          className="send-btn"
          onClick={handleSend}
          disabled={isSending || !inputValue.trim()}
          title="Send message"
        >
          ➤
        </button>
      </div>

      {/* Payment Modal */}
      {paymentModalData && (
        <PaymentModal
          userId={userId}
          sessionId={sessionId}
          paymentData={paymentModalData}
          onClose={onClosePaymentModal}
          onConfirm={onConfirmPayment}
          cardInfo={userInfo?.cardInfo}
          userInfo={userInfo}
          updateUserInfo={updateUserInfo}
        />
      )}
    </div>
  );
}

export default ChatContainer;
